import { WhatsAppMessage, ChatAnalysis } from '@/types/whatsapp'
import { format } from 'date-fns'

/**
 * Project-related keywords used to categorize messages.
 * Each key represents a category; each value is an array of synonyms/phrases.
 */
const PROJECT_TERMS = {
  decisions: ['approved', 'agreed', 'confirmed', 'signed off', 'authorized'],
  deadlines: ['due by', 'deadline', 'due date', 'by', 'until'],
  costs: ['£', '$', '€', 'cost', 'price', 'quote', 'budget', 'variation', 'additional'],
  quantities: ['units', 'qty', 'quantity', 'm2', 'm3', 'tonnes'],
  issues: ['issue', 'problem', 'concern', 'risk', 'blocked'],
  delays: ['delay', 'postpone', 'late', 'behind', 'reschedule', 'push back'],
  actions: ['action', 'task', 'todo', 'follow up', 'review'],
} as const

/**
 * Additional categories for reasons behind delays.
 */
const DELAY_REASONS = {
  weather: ['rain', 'weather', 'storm', 'wind'],
  materials: ['delivery', 'material', 'supply', 'shortage'],
  labor: ['staff', 'worker', 'labour', 'resource'],
  equipment: ['equipment', 'machinery', 'plant', 'tool'],
  approvals: ['approval', 'permit', 'permission', 'sign-off'],
} as const

/**
 * Additional categories for new/extra work items.
 */
const WORK_TYPES = {
  structural: ['steel', 'concrete', 'foundation', 'beam'],
  finishes: ['paint', 'finish', 'decoration', 'carpet'],
  services: ['electrical', 'plumbing', 'mechanical', 'hvac'],
  external: ['landscaping', 'paving', 'drainage', 'facade'],
} as const

/** Non-noun words to exclude from the key topics. */
const NON_NOUNS = new Set([
  // Articles
  'the', 'a', 'an',
  // Common WhatsApp words
  'attached', 'media', 'image', 'video', 'audio', 'file',
  'omitted', 'message', 'sent', 'received', 'deleted',
  // Pronouns
  'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them',
  // Verbs (common forms)
  'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had',
  'do', 'does', 'did', 'will', 'would', 'can', 'could', 'should',
  // Prepositions
  'in', 'on', 'at', 'to', 'for', 'with', 'by', 'from', 'of',
  // Conjunctions
  'and', 'or', 'but', 'if', 'when', 'while', 'because',
  // Adjectives
  'good', 'bad', 'big', 'small', 'new', 'old',
  // Common time-related words
  'today', 'tomorrow', 'yesterday', 'now', 'later',
  // Numbers and units
  'one', 'two', 'three', 'first', 'second', 'third',
  // Chat-specific words
  'hi', 'hello', 'hey', 'thanks', 'please', 'ok', 'okay',
  // Common verbs in project context
  'need', 'want', 'get', 'take', 'make', 'see', 'look',
  'send', 'check', 'update', 'confirm', 'approve',
])

/** Noun endings to help identify noun-like words. */
const NOUN_ENDINGS = [
  'tion', 'ment', 'ence', 'ance', 'age', 'ity', 'ness',
  'ship', 'hood', 'ism', 'ing', 'er', 'or', 'ist',
]

/** Known project nouns to always include in "key topics." */
const PROJECT_NOUNS = new Set([
  // Construction materials
  'concrete', 'steel', 'timber', 'brick', 'block', 'glass',
  // Building elements
  'foundation', 'wall', 'floor', 'roof', 'beam', 'column',
  'slab', 'window', 'door', 'frame', 'ceiling', 'pipe',
  // Equipment
  'scaffold', 'crane', 'equipment', 'tool', 'plant', 'machine',
  // Resources
  'material', 'labour', 'worker', 'contractor', 'supplier',
  // Personnel
  'engineer', 'architect', 'client', 'manager', 'inspector',
  'foreman', 'supervisor', 'surveyor', 'plumber', 'electrician',
  // Documentation
  'permit', 'approval', 'drawing', 'plan', 'specification',
  'document', 'report', 'certificate', 'invoice', 'quote',
  // Project management
  'schedule', 'programme', 'budget', 'cost', 'payment',
  'variation', 'claim', 'delay', 'progress', 'quality',
  'safety', 'risk', 'issue', 'problem', 'solution',
  // Areas/Locations
  'site', 'area', 'zone', 'level', 'floor', 'room',
  'section', 'building', 'structure', 'office', 'yard',
])

/**
 * Main analysis function to extract all relevant stats from a list of WhatsApp messages.
 */
export async function analyzeChatData(messages: WhatsAppMessage[]): Promise<ChatAnalysis> {
  if (!messages?.length) {
    throw new Error('No messages provided for analysis')
  }

  const totalMessages = messages.length
  const participants = new Set(messages.map((m) => m.sender))
  const activeParticipants = participants.size

  // Sort participant stats by highest message count
  const participantStats = Array.from(participants).map((name) => {
    const userMessages = messages.filter((m) => m.sender === name)
    return {
      name,
      messageCount: userMessages.length,
      firstMessage: userMessages[0]?.timestamp,
      lastMessage: userMessages[userMessages.length - 1]?.timestamp,
      averageResponseTime: calculateAverageResponseTime(userMessages),
    }
  })
  participantStats.sort((a, b) => b.messageCount - a.messageCount)

  // Higher-level analytics
  const activityPatterns = analyzeActivityPatterns(messages)
  const decisions = extractProjectDecisions(messages)
  const messagesByDate = aggregateMessagesByDate(messages)
  const keyTopics = analyzeKeyTopics(messages)
  const delayAnalysis = analyzeDelays(messages)
  const additionalWorkAnalysis = analyzeAdditionalWork(messages)

  return {
    totalMessages,
    activeParticipants,
    peakActivityTime: activityPatterns.peakHour,
    keyTopics,
    messagesByDate,
    participantStats,
    decisions,
    activityPatterns,
    delayAnalysis,
    additionalWorkAnalysis,
    messages: messages.map((msg) => ({
      ...msg,
      dateTime: format(msg.timestamp, 'dd MMM yyyy HH:mm'),
    })),
  }
}

/**
 * Analyzes overall chat activity by hour/day to determine peak usage times.
 */
function analyzeActivityPatterns(messages: WhatsAppMessage[]) {
  const hourlyActivity = new Array(24).fill(0)
  const dailyActivity = new Map<string, number>()

  let maxActivityCount = 0
  let peakHourIndex = 0

  messages.forEach((msg) => {
    const hour = msg.timestamp.getHours()
    const day = msg.timestamp.toISOString().split('T')[0]

    hourlyActivity[hour] += 1
    if (hourlyActivity[hour] > maxActivityCount) {
      maxActivityCount = hourlyActivity[hour]
      peakHourIndex = hour
    }

    dailyActivity.set(day, (dailyActivity.get(day) || 0) + 1)
  })

  // Format a one-hour window (peakHourIndex to nextHourIndex)
  const nextHourIndex = (peakHourIndex + 1) % 24
  const formatHour = (h: number) => h.toString().padStart(2, '0') + ':00'
  const peakHour = `${formatHour(peakHourIndex)} - ${formatHour(nextHourIndex)}`

  // Identify the single busiest day by message count
  const peakDayEntry = Array.from(dailyActivity.entries()).sort((a, b) => b[1] - a[1])[0]
  const peakDay = peakDayEntry?.[0] ?? null

  return {
    hourlyActivity,
    peakHour,
    peakDay,
  }
}

/**
 * Extracts project decisions/events from messages by looking for project-related keywords.
 */
function extractProjectDecisions(messages: WhatsAppMessage[]) {
  const decisions = []

  for (const msg of messages) {
    const content = msg.content.toLowerCase()

    // If any category's keywords appear, record that message as a "decision/event"
    for (const [type, terms] of Object.entries(PROJECT_TERMS)) {
      if (terms.some((term) => content.includes(term))) {
        decisions.push({
          date: msg.timestamp,
          sender: msg.sender,
          content: msg.content,
          type: type as 'decision' | 'deadline' | 'cost' | 'issue' | 'action',
        })
        break
      }
    }
  }

  // Sort in descending order by date
  decisions.sort((a, b) => b.date.getTime() - a.date.getTime())
  return decisions
}

/**
 * Identifies the top 10 key topics (likely nouns) mentioned in the messages.
 */
function analyzeKeyTopics(messages: WhatsAppMessage[]): string[] {
  const wordFrequency = new Map<string, number>()

  for (const msg of messages) {
    const words = msg.content
      .toLowerCase()
      .split(/\s+/)
      .map((w) => w.replace(/[.,!?]+$/, '')) // remove trailing punctuation
      .filter((w) => isValidTopicWord(w))

    for (const w of words) {
      wordFrequency.set(w, (wordFrequency.get(w) || 0) + 1)
    }
  }

  // Sort by highest frequency, then return top 10
  return Array.from(wordFrequency.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([word]) => word)
}

/**
 * Checks if a given word is likely a relevant noun for "key topics."
 */
function isValidTopicWord(word: string): boolean {
  if (word.length < 4) return false
  if (NON_NOUNS.has(word)) return false
  if (/^[0-9]+$/.test(word)) return false // purely numeric

  // Project-specific known nouns
  if (PROJECT_NOUNS.has(word)) return true

  // Check for typical noun endings
  if (NOUN_ENDINGS.some((ending) => word.endsWith(ending))) return true

  // Capitalized words (possible proper nouns), but skip if length < 2
  if (word[0] === word[0].toUpperCase() && word.length > 1) return true

  return false
}

/**
 * Calculates average response time between consecutive messages sent by the same participant.
 * Only counts gaps under 1 hour as "responses."
 */
function calculateAverageResponseTime(messages: WhatsAppMessage[]): number {
  if (messages.length < 2) return 0

  const responseTimes: number[] = []

  for (let i = 1; i < messages.length; i++) {
    const prev = messages[i - 1]
    const curr = messages[i]

    // Only count if the same participant or 
    // if we specifically want the gap between different participants, adjust logic:
    if (curr.sender === prev.sender) continue // skip if the same sender

    const diff = curr.timestamp.getTime() - prev.timestamp.getTime()
    if (diff > 0 && diff < 3600000) {
      responseTimes.push(diff)
    }
  }

  if (!responseTimes.length) return 0
  const sum = responseTimes.reduce((acc, val) => acc + val, 0)
  return sum / responseTimes.length
}

/**
 * Aggregates the total number of messages per day, and includes which project topics appear.
 */
function aggregateMessagesByDate(messages: WhatsAppMessage[]) {
  const dailyMessages = new Map<string, { count: number; topics: Set<string> }>()

  for (const msg of messages) {
    const date = msg.timestamp.toISOString().split('T')[0]
    const foundTopics = extractTopicsFromMessage(msg.content.toLowerCase())

    if (!dailyMessages.has(date)) {
      dailyMessages.set(date, { count: 0, topics: new Set() })
    }

    const day = dailyMessages.get(date)!
    day.count++
    foundTopics.forEach((t) => day.topics.add(t))
  }

  return Array.from(dailyMessages.entries())
    .map(([date, { count, topics }]) => ({
      date,
      count,
      topics: Array.from(topics),
    }))
    .sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * Checks a message for any project terms and returns the matching categories.
 */
function extractTopicsFromMessage(content: string): string[] {
  const categories: string[] = []

  for (const [category, terms] of Object.entries(PROJECT_TERMS)) {
    if (terms.some((term) => content.includes(term))) {
      categories.push(category)
    }
  }

  return categories
}

/**
 * Analyzes messages that mention delays, capturing total delays, average duration, reasons, etc.
 */
function analyzeDelays(messages: WhatsAppMessage[]) {
  const delays: { date: Date; duration?: number; reason?: string }[] = []
  const delayTypes = new Map<string, number>()

  for (const msg of messages) {
    const content = msg.content.toLowerCase()
    const hasDelayKeywords = PROJECT_TERMS.delays.some((term) => content.includes(term))
    if (!hasDelayKeywords) continue

    // Attempt to extract a duration (e.g., "2 days", "1 week behind", etc.)
    const duration = extractDelayDuration(content)

    // Attempt to identify a reason (e.g., "weather", "materials")
    const reason = identifyDelayReason(content)
    if (reason) {
      delayTypes.set(reason, (delayTypes.get(reason) || 0) + 1)
    }

    delays.push({ date: msg.timestamp, duration, reason })
  }

  const delayTrend = aggregateByDate(delays.map((d) => d.date))

  // Only compute average from those that have a duration
  const validDurations = delays.filter((d) => d.duration).map((d) => d.duration!)
  const averageDuration =
    validDurations.reduce((acc, val) => acc + val, 0) / (validDurations.length || 1)

  return {
    totalDelays: delays.length,
    averageDelayDuration: averageDuration || 0,
    delaysByType: Object.fromEntries(delayTypes),
    delayTrend,
    mostCommonReasons: Array.from(delayTypes.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([reason]) => reason),
  }
}

/**
 * Attempts to parse the numerical delay duration (in days) from a message string.
 */
function extractDelayDuration(content: string): number | undefined {
  // Matches e.g. "2 day(s)", "1 week", "3 weeks", "1 month"
  const match = content.match(/(\d+)\s*(day|week|month)s?/)
  if (!match) return undefined

  const [, amountStr, unit] = match
  const amount = parseInt(amountStr, 10)
  if (Number.isNaN(amount)) return undefined

  switch (unit) {
    case 'week':
      return amount * 7
    case 'month':
      return amount * 30
    default:
      // default to 'day'
      return amount
  }
}

/**
 * Identifies a delay reason category (weather, materials, labour, equipment, approvals, etc.)
 */
function identifyDelayReason(content: string): string {
  for (const [reasonCategory, terms] of Object.entries(DELAY_REASONS)) {
    if (terms.some((term) => content.includes(term))) {
      return reasonCategory
    }
  }
  return 'other'
}

/**
 * Analyzes any messages referencing "additional work" or "variation," capturing type, approval, etc.
 */
function analyzeAdditionalWork(messages: WhatsAppMessage[]) {
  const additionalWork: {
    date: Date
    value?: number
    type?: string
    status?: 'approved' | 'pending' | 'rejected'
  }[] = []

  let totalValue = 0
  const typeBreakdown = new Map<string, number>()
  const approvalStatus = { approved: 0, pending: 0, rejected: 0 }

  for (const msg of messages) {
    const content = msg.content.toLowerCase()
    if (!content.includes('variation') && !content.includes('additional work')) continue

    // Extract monetary value if present (e.g. £1,200.50)
    const valueMatch = content.match(/[£$€](\d+(?:,\d{3})*(?:\.\d{2})?)/)
    const numericValue = valueMatch
      ? parseFloat(valueMatch[1].replace(/,/g, ''))
      : undefined

    // Identify the type of extra work (structural, finishes, etc.)
    const extraType = identifyWorkType(content)
    if (extraType) {
      typeBreakdown.set(extraType, (typeBreakdown.get(extraType) || 0) + 1)
    }

    // Determine approval status from the text
    let status: 'approved' | 'pending' | 'rejected' = 'pending'
    if (content.includes('approved')) status = 'approved'
    if (content.includes('rejected')) status = 'rejected'
    approvalStatus[status]++

    // Update total value
    if (numericValue) {
      totalValue += numericValue
    }

    additionalWork.push({
      date: msg.timestamp,
      value: numericValue,
      type: extraType,
      status,
    })
  }

  const valueByMonth = aggregateValueByMonth(additionalWork)

  return {
    totalItems: additionalWork.length,
    totalValue,
    approvalStatus,
    typeBreakdown: Object.fromEntries(typeBreakdown),
    valueByMonth,
  }
}

/**
 * Identifies extra work type (structural, finishes, services, external) based on message content.
 */
function identifyWorkType(content: string): string {
  for (const [category, terms] of Object.entries(WORK_TYPES)) {
    if (terms.some((term) => content.includes(term))) {
      return category
    }
  }
  return 'other'
}

/**
 * Aggregates an array of Dates into a daily count.
 */
function aggregateByDate(dates: Date[]) {
  const counts = new Map<string, number>()

  for (const date of dates) {
    const dateStr = date.toISOString().split('T')[0]
    counts.set(dateStr, (counts.get(dateStr) || 0) + 1)
  }

  return Array.from(counts.entries())
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * Aggregates monetary values by calendar month (YYYY-MM).
 */
function aggregateValueByMonth(items: { date: Date; value?: number }[]) {
  const monthlyValues = new Map<string, number>()

  for (const item of items) {
    if (!item.value) continue
    const monthKey = item.date.toISOString().slice(0, 7) // YYYY-MM
    monthlyValues.set(monthKey, (monthlyValues.get(monthKey) || 0) + item.value)
  }

  return Array.from(monthlyValues.entries())
    .map(([month, value]) => ({ month, value }))
    .sort((a, b) => a.month.localeCompare(b.month))
}
